(function () {
  'use strict';

  if (!window.customElements) return;
  if (customElements.get('product-form')) return;

  const routes = (/** @type {{ routes?: { cart_add_url?: string, cart_url?: string } }} */ (window)).routes || {};
  const cartAddUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = routes.cart_url || '/cart';
  const GTENDER_PRODUCT_ID = '15645008920657';
  const GTENDER_VARIANT_ID = '58537722183761';
  const GTENDER_SELLING_PLAN_ID = '713169141841';
  const GTENDER_ACCESS_URL = 'https://api.glhksolution.com/v1/gtender/access';
  const GTENDER_TIMEOUT_MS = 10000;

  /** @typedef {{ method?: string, headers: Record<string, string>, body?: FormData }} ProductCartRequestConfig */
  const nativeFetchConfig = (/** @type {{ fetchConfig?: (mode?: string) => ProductCartRequestConfig }} */ (window)).fetchConfig;
  /** @type {(mode?: string) => ProductCartRequestConfig} */
  const fetchConfig =
    nativeFetchConfig ||
    function () {
      return {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    };

  const nativePublish = /** @type {{ publish?: (name: string, payload: unknown) => unknown }} */ (window).publish;
  const publish = typeof nativePublish === 'function'
    ? nativePublish.bind(window)
    : function publishFallback(/** @type {string} */ eventName, /** @type {unknown} */ payload) {
        document.dispatchEvent(
          new CustomEvent(eventName, {
            detail: payload || {}
          })
        );

        return Promise.resolve();
      };

  const nativePubSubEvents = /** @type {{ PUB_SUB_EVENTS?: { cartError?: string, cartUpdate?: string } }} */ (window).PUB_SUB_EVENTS;
  const PUB_SUB_EVENTS = Object.freeze({
    cartError: nativePubSubEvents?.cartError || 'cart-error',
    cartUpdate: nativePubSubEvents?.cartUpdate || 'cart-update',
  });

  const nativeCartPerformance = /** @type {{ CartPerformance?: {
    createStartingMarker: (...args: unknown[]) => number,
    measureFromMarker: (...args: unknown[]) => void,
    measure: (name: string, callback?: () => void) => void,
    measureFromEvent: (...args: unknown[]) => void
  } }} */ (window).CartPerformance;
  const CartPerformance =
    nativeCartPerformance ||
    {
      createStartingMarker: function (/** @type {unknown[]} */ ..._args) { return 0; },
      measureFromMarker: function (/** @type {unknown[]} */ ..._args) {},
      measure: function (/** @type {string} */ _n, /** @type {(() => void) | undefined} */ fn) { if (typeof fn === 'function') fn(); },
      measureFromEvent: function (/** @type {unknown[]} */ ..._args) {},
    };

  const GCITY = (/** @type {{ GCITY?: { analytics?: { track?: (event: string, payload: Record<string, unknown>) => void } } }} */ (window)).GCITY || null;
  /** @typedef {{ ready?: () => Promise<unknown> | void, getState?: () => { status?: string } | null, authenticatedFetch?: typeof fetch }} LittleGSession */
  /** @type {Promise<LittleGSession> | null} */
  let webSessionLoad = null;
  let cartGuardRun = 0;

  function isZh() {
    return (document.documentElement.lang || '').toLowerCase().startsWith('zh');
  }

  function dispatchCartEvents(/** @type {Record<string, unknown>} */ cartData, /** @type {Record<string, unknown>} */ meta) {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { ...cartData, ...meta } }));
    } catch (_e) {
      // Optional cart event listeners must never interrupt Shopify checkout.
    }
  }

  function productFormAssetUrl(/** @type {string} */ assetName) {
    const scripts = Array.from(document.scripts || []);
    const owner = scripts.find((script) => {
      try {
        return new URL(script.src, window.location.href).pathname.endsWith('/product-form.js');
      } catch (_e) {
        // Ignore unrelated scripts with malformed source URLs.
        return false;
      }
    });
    if (!owner?.src) return '';
    try {
      const url = new URL(owner.src, window.location.href);
      url.pathname = url.pathname.replace(/\/product-form\.js$/, `/${assetName}`);
      return url.href;
    } catch (_e) {
      // The native session loader fails closed when no valid asset URL exists.
      return '';
    }
  }

  /** @returns {Promise<LittleGSession>} */
  function ensureLittleGWebSession() {
    const nativeSession = (/** @type {{ LittleGWebSession?: LittleGSession }} */ (window)).LittleGWebSession;
    if (nativeSession) return Promise.resolve(nativeSession);
    if (webSessionLoad !== null) return webSessionLoad;
    const src = productFormAssetUrl('littleg-web-session.js');
    if (!src) return Promise.reject(new Error('native_session_asset_unavailable'));
    const existing = Array.from(document.scripts || []).find((script) => {
      try { return new URL(script.src, window.location.href).pathname.endsWith('/littleg-web-session.js'); }
      catch (_e) { /* Ignore scripts with invalid URLs. */ return false; }
    });
    webSessionLoad = new Promise((resolve, reject) => {
      const finish = () => (/** @type {{ LittleGWebSession?: LittleGSession }} */ (window)).LittleGWebSession
        ? resolve((/** @type {{ LittleGWebSession?: LittleGSession }} */ (window)).LittleGWebSession)
        : reject(new Error('native_session_unavailable'));
      if (existing) {
        if ((/** @type {{ LittleGWebSession?: LittleGSession }} */ (window)).LittleGWebSession) return finish();
        existing.addEventListener('load', finish, { once: true });
        existing.addEventListener('error', () => reject(new Error('native_session_asset_failed')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      script.dataset.runtimeOwner = 'assets/littleg-web-session.js';
      script.dataset.runtimeContract = 'LITTLEG-WEB-AUTH-SESSION-03';
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => reject(new Error('native_session_asset_failed')), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      webSessionLoad = null;
      throw error;
    });
    return webSessionLoad;
  }

  async function gtenderPurchaseEligibility() {
    let session;
    try {
      session = await ensureLittleGWebSession();
      if (typeof session.ready === 'function') await session.ready();
    } catch (_error) {
      // Unverified identity cannot authorize GTender purchase.
      return { allowed: false, code: 'session_unavailable' };
    }
    const state = typeof session.getState === 'function' ? session.getState() : null;
    if (!state || String(state.status || '').toLowerCase() !== 'authenticated') {
      return { allowed: false, code: 'session_required' };
    }
    if (typeof session.authenticatedFetch !== 'function') {
      return { allowed: false, code: 'session_unavailable' };
    }

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), GTENDER_TIMEOUT_MS) : 0;
    try {
      const response = await session.authenticatedFetch(GTENDER_ACCESS_URL, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        ...(controller ? { signal: controller.signal } : {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success !== true || !payload.data) {
        return { allowed: false, code: 'access_unavailable' };
      }
      if (String(payload.data.mode || '').toLowerCase() === 'pro' && payload.data.pro_entitled === true) {
        return { allowed: false, code: 'already_pro' };
      }
      return { allowed: true, code: 'eligible' };
    } catch (_error) {
      // Keep Pro purchase disabled when backend eligibility cannot be verified.
      return { allowed: false, code: 'access_unavailable' };
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  async function readCartSnapshot() {
    const target = new URL(cartUrl || '/cart', window.location.origin);
    const cartPathname = target.pathname;
    let lastNonSlash = cartPathname.length;
    while (lastNonSlash > 0 && cartPathname[lastNonSlash - 1] === '/') lastNonSlash -= 1;
    target.pathname = cartPathname.slice(0, lastNonSlash) + '.js';
    target.search = '';
    target.hash = '';
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), GTENDER_TIMEOUT_MS) : 0;
    try {
      const response = await fetch(target.href, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'error',
        headers: { Accept: 'application/json' },
        ...(controller ? { signal: controller.signal } : {}),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload || !Array.isArray(payload.items)) throw new Error('cart_snapshot_unavailable');
      return payload;
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  /** @typedef {{ product_id?: string | number, quantity?: number }} ShopifyCartItem */
  /** @param {{ items?: ShopifyCartItem[] } | null} cart */
  function gtenderCartState(cart) {
    /** @type {ShopifyCartItem[]} */
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const gtender = items.filter((item) => String(item?.product_id) === GTENDER_PRODUCT_ID);
    const others = items.filter((item) => String(item?.product_id) !== GTENDER_PRODUCT_ID);
    const quantity = gtender.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);
    return {
      hasGtender: gtender.length > 0,
      duplicate: gtender.length > 1 || quantity > 1,
      mixed: gtender.length > 0 && others.length > 0,
      validSubscriptionCart: gtender.length === 1 && quantity === 1 && others.length === 0,
    };
  }

  /** @param {Element | null} row */
  function cartRowIsGtender(row) {
    if (!row) return false;
    if (String(row instanceof HTMLElement ? row.dataset.productId || '' : row.getAttribute('data-product-id') || '') === GTENDER_PRODUCT_ID) return true;
    const productLink = row.querySelector('a.cart-item__name[href]');
    return !!(productLink && /\/products\/gtender-pro(?:[/?#]|$)/.test(productLink.getAttribute('href') || ''));
  }

  function lockGtenderQuantityControls() {
    const rows = document.querySelectorAll('.cart-item');
    rows.forEach((row) => {
      if (!cartRowIsGtender(row)) return;
      const input = row.querySelector('input.quantity__input[name="updates[]"]');
      if (input instanceof HTMLInputElement) {
        input.value = '1';
        input.min = '1';
        input.max = '1';
        input.dataset.gtenderQuantityLocked = 'true';
      }
      row.querySelectorAll('quantity-input .quantity__button').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.gtenderQuantityLocked = 'true';
      });
    });
  }

  /** @param {boolean} blocked @param {string} code */
  function setCheckoutGuard(blocked, code) {
    const controls = document.querySelectorAll('button[name="checkout"], a[href*="/checkout"]');
    controls.forEach((control) => {
      if (blocked) {
        if (control instanceof HTMLElement) control.dataset.gtenderCheckoutBlocked = code || 'invalid_cart';
        control.setAttribute('aria-disabled', 'true');
        if (control instanceof HTMLButtonElement) control.disabled = true;
      } else if (control instanceof HTMLElement && control.dataset.gtenderCheckoutBlocked !== undefined) {
        delete control.dataset.gtenderCheckoutBlocked;
        control.removeAttribute('aria-disabled');
        if (control instanceof HTMLButtonElement) control.disabled = false;
      }
    });
    const error = document.getElementById('cart-errors') || document.getElementById('CartDrawer-LiveRegionText');
    if (error) {
      error.textContent = blocked
        ? (isZh()
          ? 'GTender Pro 必須以單一月費方案獨立結帳，數量固定為 1。請移除其他商品或多餘訂閱後再繼續。'
          : 'GTender Pro must be checked out separately as one monthly subscription. Remove other items or extra subscription quantities before continuing.')
        : '';
    }
  }

  async function applyGtenderCartGuard() {
    const run = ++cartGuardRun;
    const hasCartSurface = !!document.querySelector('[data-shell-page="cart"], cart-drawer');
    if (!hasCartSurface) return;
    lockGtenderQuantityControls();
    try {
      const cart = await readCartSnapshot();
      if (run !== cartGuardRun) return;
      const state = gtenderCartState(cart);
      setCheckoutGuard(state.hasGtender && !state.validSubscriptionCart, state.mixed ? 'mixed_cart' : 'invalid_quantity');
      if (state.hasGtender) lockGtenderQuantityControls();
    } catch (_error) {
      // Failed cart reads cannot silently unblock a displayed GTender subscription.
      const gtenderInDom = Array.from(document.querySelectorAll('.cart-item')).some(cartRowIsGtender);
      if (gtenderInDom) setCheckoutGuard(true, 'cart_verification_unavailable');
    }
  }

  function scheduleGtenderCartGuard() {
    window.setTimeout(() => { void applyGtenderCartGuard(); }, 0);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const blocked = event.target.closest('[data-gtender-checkout-blocked]');
    if (blocked) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.dataset.gtenderQuantityLocked !== 'true') return;
    if (event.target.value !== '1') event.target.value = '1';
  }, true);

  document.addEventListener('cart:updated', scheduleGtenderCartGuard);
  document.addEventListener('cart:item-added', scheduleGtenderCartGuard);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleGtenderCartGuard, { once: true });
  else scheduleGtenderCartGuard();

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
        if (!this.form) return;
        this.submitButton = /** @type {HTMLButtonElement | HTMLInputElement | null} */ (this.querySelector('[type="submit"]'));
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.hideErrors = String(this.dataset.hideErrors) === 'true';
        this.error = false;
        const vInput = this.variantIdInput;
        if (vInput instanceof HTMLInputElement) vInput.disabled = false;
        this.cart = /** @type {(HTMLElement & { getSectionsToRender?: () => Array<{id: string}>, setActiveElement?: (element: Element | null) => void, renderContents?: (response: unknown) => void }) | null} */ (document.querySelector('cart-drawer') || document.querySelector('cart-notification'));
        if (this.cart && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('input[name="id"]') : null;
      }

      /** @param {Element | null} spinner */
      releaseSubmitState(spinner) {
        if (!this.submitButton) {
          if (spinner) spinner.classList.add('hidden');
          return;
        }
        this.submitButton.classList.remove('loading');
        if (this.submitButton.disabled) this.submitButton.setAttribute('aria-disabled', 'true');
        else this.submitButton.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      }

      /** @param {Event} evt */
      async onSubmitHandler(evt) {
        evt.preventDefault();
        // Liquid can omit the product form or its button for non-purchasable modes.
        // Never enter cart mutation with an incomplete Shopify form.
        if (!this.form || !this.submitButton) return;
        if (this.submitButton.disabled ||
            this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.error = false;
        this.handleErrorMessage('');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        const config = fetchConfig('javascript');
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        if (config.headers['Content-Type']) delete config.headers['Content-Type'];
        const formData = new FormData(this.form);
        const gtenderPurchase = this.dataset.productId === GTENDER_PRODUCT_ID;
        if (gtenderPurchase) {
          if (this.dataset.gtenderProPurchase !== 'true' ||
              formData.get('id') !== GTENDER_VARIANT_ID ||
              formData.get('selling_plan') !== GTENDER_SELLING_PLAN_ID) {
            this.handleErrorMessage(
              isZh()
                ? '未能確認 GTender Pro 訂閱設定。請重新載入產品頁面。'
                : 'GTender Pro subscription could not be verified. Please reload the product page.'
            );
            this.releaseSubmitState(spinner);
            return;
          }

          const eligibility = await gtenderPurchaseEligibility();
          if (!eligibility.allowed) {
            const messages = {
              session_required: isZh()
                ? '請先登入 GTender 帳戶再訂閱。尚未加入任何商品。'
                : 'Sign in to your GTender account before subscribing. Nothing was added to the cart.',
              already_pro: isZh()
                ? '此 GTender 帳戶已啟用 Pro，無需重複訂閱。請直接返回工作區。'
                : 'GTender Pro is already active for this account. Open your workspace instead of subscribing again.',
              session_unavailable: isZh()
                ? '暫時無法載入 GTender 帳戶驗證。尚未加入任何商品，請稍後再試。'
                : 'GTender account verification is temporarily unavailable. Nothing was added; try again later.',
              access_unavailable: isZh()
                ? '暫時無法確認你的 GTender 方案。尚未加入任何商品，請稍後再試。'
                : 'We cannot verify your GTender plan right now. Nothing was added; try again later.',
            };
            const messageKey = /** @type {keyof typeof messages} */ (eligibility.code);
            this.handleErrorMessage(messages[messageKey] !== undefined ? messages[messageKey] : messages.access_unavailable);
            this.releaseSubmitState(spinner);
            return;
          }

          let cartBefore;
          try {
            cartBefore = await readCartSnapshot();
          } catch (_error) {
            this.handleErrorMessage(
              isZh()
                ? '暫時無法確認購物車狀態。尚未加入訂閱，請稍後再試。'
                : 'We cannot verify the cart right now. The subscription was not added; try again later.'
            );
            this.releaseSubmitState(spinner);
            return;
          }
          const stateBefore = gtenderCartState(cartBefore);
          if (stateBefore.hasGtender || (Array.isArray(cartBefore.items) && cartBefore.items.length > 0)) {
            this.handleErrorMessage(
              stateBefore.hasGtender
                ? (isZh()
                  ? '購物車已有 GTender Pro 訂閱。請先檢查購物車，不會再次加入。'
                  : 'GTender Pro is already in the cart. Review the cart instead of adding it again.')
                : (isZh()
                  ? 'GTender Pro 必須獨立結帳。請先完成或清空現有購物車。'
                  : 'GTender Pro must be checked out separately. Complete or clear the current cart first.')
            );
            this.releaseSubmitState(spinner);
            return;
          }
          formData.set('quantity', '1');
        } else {
          try {
            const cartBefore = await readCartSnapshot();
            if (gtenderCartState(cartBefore).hasGtender) {
              this.handleErrorMessage(
                isZh()
                  ? 'GTender Pro 必須獨立結帳。請先完成或移除該訂閱後再加入其他商品。'
                  : 'GTender Pro must be checked out separately. Complete or remove that subscription before adding other items.'
              );
              this.releaseSubmitState(spinner);
              return;
            }
          } catch (_error) {
            // Normal products preserve existing behavior when no GTender state can be confirmed.
          }
        }

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          try {
            const sections = this.cart.getSectionsToRender().map((s) => s.id);
            formData.append('sections', sections.join(','));
            formData.append('sections_url', window.location.pathname);
            if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
          } catch (_e) {
            // Rendering is optional; retain the Shopify add-to-cart request.
          }
        }

        config.body = formData;
        fetch(cartAddUrl, config)
          .then((r) => r.json())
          .then((response) => {
            if (response?.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || '');
              const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
              if (soldOutMessage && this.submitButtonText) {
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            if (gtenderPurchase) {
              const item = response && Array.isArray(response.items) ? response.items[0] : response;
              const returnedPlan = item?.selling_plan_allocation?.selling_plan;
              if (!item || String(item.variant_id || item.id) !== GTENDER_VARIANT_ID ||
                  !returnedPlan || String(returnedPlan.id) !== GTENDER_SELLING_PLAN_ID ||
                  Number(item.quantity) !== 1) {
                const sections = this.cart && typeof this.cart.getSectionsToRender === 'function'
                  ? this.cart.getSectionsToRender() : [];
                const inspectionReady = sections.length > 0 && response.sections &&
                  sections.every((section) => typeof response.sections[section.id] === 'string' &&
                    response.sections[section.id].trim().length > 0);
                if (inspectionReady && this.cart && typeof this.cart.renderContents === 'function') {
                  this.cart.renderContents(response);
                } else {
                  this.error = true;
                  const inspectionUrl = new URL((/** @type {{ routes?: { cart_url?: string } }} */ (window)).routes?.cart_url || cartUrl || '/cart', window.location.origin);
                  inspectionUrl.searchParams.set('subscription_check', 'required');
                  window.location.assign(inspectionUrl.href);
                  return;
                }
                throw new Error('GTender cart result does not confirm the expected subscription; inspect cart before retrying');
              }
            }

            if (!this.cart) {
              const safeCartUrl = (/** @type {{ routes?: { cart_url?: string } }} */ (window)).routes?.cart_url || cartUrl || '/cart';
              window.location.assign(`${safeCartUrl}?open_cart=true`);
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const variantId = formData.get('id');
            const p = this.error
              ? Promise.resolve()
              : publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: response,
                });

            return Promise.resolve(p).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              this.error = false;
              const quickAddModal = /** @type {(HTMLElement & { hide?: (animate?: boolean) => void }) | null} */ (this.closest('quick-add-modal'));
              if (quickAddModal && typeof quickAddModal.hide === 'function') {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure('add:paint-updated-sections', () => {
                        if (this.cart && typeof this.cart.renderContents === 'function') this.cart.renderContents(response);
                      });
                    }, 0);
                  },
                  { once: true }
                );

                quickAddModal.hide(true);
              } else {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  if (this.cart && typeof this.cart.renderContents === 'function') this.cart.renderContents(response);
                });
              }

              dispatchCartEvents(response, { source: 'product-form', productVariantId: variantId });
              try {
                if (GCITY?.analytics?.track) {
                  GCITY.analytics.track('add_to_cart', {
                    product_id: this.dataset.productId,
                    variant_id: variantId,
                    quantity: formData.get('quantity') || 1,
                    source: 'product-form',
                  });
                }
              } catch (_e) {}
            });
          })
          .catch((e) => {
            this.error = true;
            this.handleErrorMessage(
              isZh()
                ? '未能確認加入購物車的結果。請先檢查購物車，再決定是否重試。'
                : 'We could not confirm whether the item was added. Check your cart before trying again.'
            );
            try { console.error(e); } catch (_e) {}
          })
          .finally(() => {
            this.releaseSubmitState(spinner);
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            CartPerformance.measureFromEvent('add:user-action', evt);
            scheduleGtenderCartGuard();
          });
      }

      /** @param {boolean} [disable=true] @param {string} [text] */
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }

        if (text && this.submitButtonText) {
          this.submitButtonText.textContent = text;
        } else if (!disable && this.submitButtonText && (/** @type {{ variantStrings?: { addToCart?: string } }} */ (window)).variantStrings?.addToCart) {
          this.submitButtonText.textContent = (/** @type {{ variantStrings?: { addToCart?: string } }} */ (window)).variantStrings?.addToCart || '';
        }

        if (!disable) {
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (soldOutMessage) soldOutMessage.classList.add('hidden');
          if (this.submitButtonText) this.submitButtonText.classList.remove('hidden');
        }
      }

      handleErrorMessage(errorMessage = '') {
        if (this.hideErrors) return;
        const errorEl = this.querySelector('.product-form__error-message-wrapper');
        if (!errorEl) return;
        const errorText = errorEl.querySelector('.product-form__error-message');
        if (errorMessage) {
          if (errorText) errorText.textContent = errorMessage;
          errorEl.removeAttribute('hidden');
          errorEl.classList.remove('hidden');
        } else {
          if (errorText) errorText.textContent = '';
          errorEl.setAttribute('hidden', '');
          errorEl.classList.add('hidden');
        }
      }
    }
  );
})();
